import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Sparkles,
  Globe,
  Zap,
  Target,
  TrendingUp,
  Bot,
  ArrowRight
} from 'lucide-react';

interface WelcomeStepProps {
  onNext: () => void;
}

const WelcomeStep: React.FC<WelcomeStepProps> = ({ onNext }) => {
  const { t } = useTranslation();

  const features = [
    {
      icon: Globe,
      title: t('onboarding.welcome.feature1Title', 'Website Analysis'),
      description: t('onboarding.welcome.feature1Desc', 'We will analyze your website to understand your business')
    },
    {
      icon: Bot,
      title: t('onboarding.welcome.feature2Title', 'AI that works for you'),
      description: t('onboarding.welcome.feature2Desc', 'Content generated automatically every day')
    },
    {
      icon: Target,
      title: t('onboarding.welcome.feature3Title', 'Discover competitors'),
      description: t('onboarding.welcome.feature3Desc', 'We identify and monitor your competitors')
    },
    {
      icon: TrendingUp,
      title: t('onboarding.welcome.feature4Title', 'Real-time metrics'),
      description: t('onboarding.welcome.feature4Desc', 'Complete dashboard with all your data')
    }
  ];

  return (
    <div className="p-5 sm:p-12">
      {/* Hero Section */}
      <div className="text-center mb-12">
        <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-primary-500 to-purple-600 mb-6 shadow-2xl shadow-primary-500/30">
          <Sparkles className="h-10 w-10 text-white" />
        </div>

        <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4">
          {t('onboarding.welcome.title', 'Welcome to')}{' '}
          <span className="bg-gradient-to-r from-primary-600 to-purple-600 bg-clip-text text-transparent">
            DevSkin Marketing
          </span>
        </h1>

        <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
          {t('onboarding.welcome.subtitle', 'Let\'s set up your account in just a few minutes. After that, your marketing platform will be ready to work for you automatically.')}
        </p>
      </div>

      {/* Features Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-12">
        {features.map((feature, index) => {
          const Icon = feature.icon;
          return (
            <div
              key={index}
              className="flex items-start space-x-4 p-4 rounded-xl bg-gray-50 dark:bg-gray-700/50 border border-gray-100 dark:border-gray-600 transition-all hover:border-primary-200 dark:hover:border-primary-500/50 hover:shadow-md"
            >
              <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                <Icon className="h-5 w-5 text-primary-600 dark:text-primary-400" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white">
                  {feature.title}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {feature.description}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Time estimate */}
      <div className="flex items-center justify-center space-x-2 text-sm text-gray-500 dark:text-gray-400 mb-8">
        <Zap className="h-4 w-4" />
        <span>{t('onboarding.welcome.timeEstimate', 'Quick setup - approximately 5 minutes')}</span>
      </div>

      {/* CTA Button */}
      <div className="flex justify-center">
        <button
          type="button"
          onClick={onNext}
          className="group w-full sm:w-auto inline-flex items-center justify-center px-8 py-3.5 sm:py-4 text-base sm:text-lg font-semibold text-white bg-gradient-to-r from-primary-600 to-purple-600 rounded-xl shadow-lg shadow-primary-500/30 hover:shadow-xl hover:shadow-primary-500/40 transition-all duration-300 hover:-translate-y-0.5"
        >
          {t('onboarding.welcome.startNow', 'Start now')}
          <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
        </button>
      </div>
    </div>
  );
};

export default WelcomeStep;
